import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from '@/features/common/Icon';
import { parseTypedDate } from '@/features/common/calendar';
import { DateRangeCalendar } from '@/features/common/DateRangeFilter/DateRangeCalendar';
import { DateRangeField } from '@/features/common/DateRangeFilter/DateRangeField';
import {
  DATE_RANGE_END,
  endToFill,
  monthOf,
  periodLabel,
  shiftedMonth,
} from '@/features/common/DateRangeFilter/dateRange';
import { useDateRangeDrafts } from '@/features/common/DateRangeFilter/useDateRangeDrafts';
import type { DateRangeEnd } from '@/features/common/DateRangeFilter/types/DateRangeEnd';
import type { DateRangeFilterProps } from '@/features/common/DateRangeFilter/types/DateRangeFilterProps';
import { escapeToTrigger } from '@/features/common/escapeToTrigger';
import { useDismiss } from '@/features/common/useDismiss';
import { dateRangeFilter } from '@/i18n/dictionaries/dateRangeFilter';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/common/dropdown.css';
import '@/features/common/DateRangeFilter/date-range-filter.css';

/**
 * A period filter — "De la" and "Până la" — that sits in a filter row as one more dropdown.
 *
 * Closed, it is a Dropdown trigger (same classes, so the same 48px box, radius and chevron as the
 * category and type beside it) whose text is the period. Open, it is MUD's date picker
 * (Components/input-date-picker.html), in MUD's own markup: the two ends as MUD date inputs —
 * `.date-picker__field` › `.input-group.input-date-wrapper` › `.input-wrapper` › `input.input` and
 * the `icon-calendar` toggle, never a browser `type="date"` — and one calendar under them,
 * `.date-picker[role=dialog]` with its header, weekdays and days. The calendar fills whichever end
 * is active: picking a day in it sets "De la" and moves on to "Până la". A date can also be typed.
 *
 * Every choice applies at once, like the other dropdowns in the row. Controlled like Dropdown
 * (`isOpen`/`onToggle`/`onClose`), so a row of them keeps one open at a time; a click outside or
 * Escape closes it.
 *
 * The MUD class names are shared with the app's older DatePicker, whose sheet styles them its own
 * way — so date-range-filter.css scopes every rule to this component and states each property the
 * other sheet also sets.
 */
export function DateRangeFilter({
  ariaLabel,
  placeholder,
  from,
  to,
  onChange,
  isOpen,
  onToggle,
  onClose,
}: DateRangeFilterProps) {
  const { t } = useTranslation(dateRangeFilter);
  const baseId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRefs = { from: useRef<HTMLInputElement>(null), to: useRef<HTMLInputElement>(null) };
  // Which end the calendar fills.
  const [active, setActive] = useState<DateRangeEnd>(DATE_RANGE_END.from);
  const [view, setView] = useState(() => monthOf(from));
  const { drafts, invalid, setDraft, setInvalidEnd } = useDateRangeDrafts(from, to);
  const value = { from, to };

  // On opening: the calendar goes to the end still missing (or "De la"), on that end's month, and
  // the focus into that end's field — the reason the panel was opened is to fill it in.
  useEffect(() => {
    if (!isOpen) return;
    const end = endToFill(from, to);
    setActive(end);
    setView(monthOf(value[end] ?? from ?? to));
    inputRefs[end].current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per opening; the ends are read as they stand at that moment
  }, [isOpen]);

  useDismiss(rootRef, isOpen, onClose);

  // The end given changes; the other stays as it stands.
  const set = (end: DateRangeEnd, iso: string | null) => {
    const next = { ...value, [end]: iso };
    onChange(next.from, next.to);
  };

  const activate = (end: DateRangeEnd) => {
    setActive(end);
    if (value[end]) setView(monthOf(value[end]));
  };

  const pick = (iso: string) => {
    set(active, iso);
    // "De la" first, then "Până la": one click per end, in the order they are read.
    if (active === DATE_RANGE_END.from) {
      setActive(DATE_RANGE_END.to);
      inputRefs.to.current?.focus();
    }
  };

  /** Reads what was typed into a field: a date sets that end, an emptied field opens it again. */
  const commit = (end: DateRangeEnd) => {
    const text = drafts[end].trim();
    if (!text) {
      setInvalidEnd(end, false);
      if (value[end]) set(end, null);
      return;
    }
    const iso = parseTypedDate(text);
    if (!iso) {
      setInvalidEnd(end, true);
      return;
    }
    setInvalidEnd(end, false);
    setView(monthOf(iso));
    if (iso !== value[end]) set(end, iso);
  };

  const shiftMonth = (delta: number) => setView((current) => shiftedMonth(current, delta));

  const hasValue = Boolean(from || to);
  const label = periodLabel(from, to, {
    any: placeholder ?? t('any'),
    fromValue: (date) => t('fromValue', { date }),
    toValue: (date) => t('toValue', { date }),
  });
  const panelId = `${baseId}-panel`;

  const field = (end: DateRangeEnd) => (
    <DateRangeField
      end={end}
      baseId={baseId}
      inputRef={inputRefs[end]}
      draft={drafts[end]}
      invalid={invalid[end]}
      active={active === end}
      onActivate={activate}
      onDraftChange={setDraft}
      onCommit={commit}
    />
  );

  return (
    <div className="dropdown-select dropdown-select--md date-range-filter" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`dropdown-select__trigger${isOpen ? ' dropdown-select__trigger--open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        // The name carries the current value too, as a listbox trigger's visible text does.
        aria-label={`${ariaLabel}: ${label}`}
        onClick={onToggle}
      >
        <span className={hasValue ? undefined : 'dropdown-select__placeholder'}>{label}</span>
        <Icon name="chevron-bottom" className="dropdown-select__chevron" />
      </button>
      {isOpen && (
        <div
          className="date-picker-panel"
          id={panelId}
          onKeyDown={escapeToTrigger(onClose, triggerRef)}
        >
          {field(DATE_RANGE_END.from)}
          {field(DATE_RANGE_END.to)}
          <DateRangeCalendar
            id={`${baseId}-calendar`}
            active={active}
            view={view}
            from={from}
            to={to}
            onShiftMonth={shiftMonth}
            onPick={pick}
          />
          <div className="date-range-filter__actions">
            <button
              type="button"
              className="btn btn--text-primary"
              disabled={!hasValue}
              onClick={() => onChange(null, null)}
            >
              {t('clear')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
